import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'

interface SsoRedirectSuccessResponse {
  code: 'success'
  data: {
    auth_url: string
    state?: string
  }
}

interface SsoRedirectFailResponse {
  code: 'fail'
  data?: {
    auth_url?: string
  }
  message?: string
}

export type SsoRedirectResponse = SsoRedirectSuccessResponse | SsoRedirectFailResponse

export interface PublicSsoConfig {
  email_password_enabled: boolean
  id: string | null
  sso_login_behavior: 'button' | 'redirect'
  sso_config: Record<string, unknown>
  sso_type: 'cas' | 'oauth2' | 'oidc' | 'saml' | null
  updated_at: string | null
  web_sso_enabled: boolean
}

interface PublicSsoConfigResponse {
  code: 'success'
  data: PublicSsoConfig
}

export interface UserSsoExchangePayload {
  password_reset_required?: boolean
  refresh_token?: string | null
  saml_attributes?: unknown
  token: string
  user: unknown
}

interface UserSsoExchangeResponse {
  code: 'success'
  data: UserSsoExchangePayload
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
})

/**
 * 解析前台 SSO callback 绝对地址。
 * IdP 必须回调到 API 服务，不应回到 Vite / SPA origin。
 */
export function resolveUserSsoCallbackUrl(apiBaseUrl = runtimeConfig.apiBaseUrl) {
  const callbackPath = '/app/auth/sso/callback'
  const normalizedBaseUrl = apiBaseUrl.trim().replace(/\/+$/, '')

  if (normalizedBaseUrl.startsWith('http')) {
    return `${normalizedBaseUrl}${callbackPath}`
  }

  const relativeBaseUrl = normalizedBaseUrl.startsWith('/')
    ? normalizedBaseUrl
    : `/${normalizedBaseUrl}`

  return new URL(`${relativeBaseUrl}${callbackPath}`, window.location.origin).toString()
}

/**
 * 请求前台 SSO 跳转地址。
 * 后端期望的是 API 前缀下的绝对回调地址，不是前端路由；
 * 这里保持和 legacy contract 一致，避免 IdP callback 落到 SPA path。
 */
export function getUserSsoRedirectUrl() {
  const redirectUri = resolveUserSsoCallbackUrl()
  return apiClient.request<SsoRedirectResponse>(
    `/app/auth/sso?redirect_uri=${encodeURIComponent(redirectUri)}`,
    {
      credentials: 'include',
    },
  )
}

export async function getPublicSsoConfig() {
  const response = await apiClient.request<PublicSsoConfigResponse>('/app/sso/config')
  return response.data
}

export function exchangeUserSsoCode(code: string) {
  return apiClient.request<UserSsoExchangeResponse>('/app/auth/sso/exchange', {
    body: JSON.stringify({ code }),
    credentials: 'include',
    method: 'POST',
  })
}
